import React from 'react';
import { Navigate } from 'react-router-dom-v5-compat';

import AuthenticationStore from '~/auth/authenticationStore';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { usePermissions } from '~/hooks/usePermissions';
import ActivationPage from '~/pages/activation';
import ChangePasswordPage from '~/pages/change-password';
import ExpiredTokenPage from '~/pages/expired-token';
import ExternalDocumentViewer from '~/pages/external-document-viewer';
import ForgotPasswordPage from '~/pages/forgot-password';
import LoginPage from '~/pages/login';
import WordOnlineErrorPage from '~/pages/word-online-invalid-subscription';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { RbacPermission } from '~/types/Rbac';
import Box from '~/ui/components/Box/Box';

interface Props {
  children: React.ReactNode;
  permissions?: RbacPermission[];
}

const AuthenticatedRoute = ({ permissions, children }: any) => {
  const { isLoading, hasSomePermissions } = usePermissions({
    permissions,
  });
  const hasPermission = hasSomePermissions;

  return (
    <ContentContainer loadingContent={{ isLoading }}>
      {/* TODO EKP-16459: cleanup and absorb padding into `eds.PageLayout` after all pages are updated. */}
      {/* @ts-ignore type theme-ui and ~/ui components */}
      <Box px={8} py={3}>
        {hasPermission ? children : <Navigate to="/unauthorized" replace />}
      </Box>
    </ContentContainer>
  );
};

const PrivateRoute = ({ children, permissions }: Props) => {
  const { location } = useRouting();
  const AS = AuthenticationStore();
  const userAuthenticated = AS.isLoggedIn();

  if (userAuthenticated) {
    return (
      <AuthenticatedRoute permissions={permissions}>
        {children}
      </AuthenticatedRoute>
    );
  }

  if (window.location.pathname === '/') {
    return <LoginPage />;
  }
  if (window.location.pathname === '/forgot-password') {
    return <ForgotPasswordPage />;
  }
  if (window.location.pathname === '/activation') {
    return <ActivationPage />;
  }
  if (window.location.pathname === '/expired-token') {
    return <ExpiredTokenPage />;
  }
  if (window.location.pathname === RoutePathType.ExternalDocumentViewer) {
    return <ExternalDocumentViewer />;
  }
  if (window.location.pathname === '/change-password') {
    return <ChangePasswordPage />;
  }
  if (window.location.pathname === '/word-online-invalid-subscription') {
    return <WordOnlineErrorPage />;
  }

  return <Navigate to="/login" state={{ from: location }} replace />;
};

export default PrivateRoute;
